import { useCallback, useState } from "react";
import { useMutation } from "@apollo/client";
import { SET_ORDER_MANAGER } from "../../api/graphql/orders";
import { Manager, managerName } from ".";

export type OrderWithManager = {
  id: number;
  orderType?: string;
  /** The order's own manager. */
  admin?: Manager | null;
  user?: { manager?: Manager | null } | null;
};

const keyOf = (order: OrderWithManager) => `${order.orderType}:${order.id}`;

/**
 * Order managers for a campaigns list. The lists are not read from the
 * Apollo cache, so a saved choice is kept here until the next load.
 */
const useOrderManagers = () => {
  const [setOrderManager] = useMutation(SET_ORDER_MANAGER);
  const [assigned, setAssigned] = useState<Record<string, Manager | null>>({});

  // Stable until a choice is saved, so a filtered list recounts only then.
  const orderManager = useCallback(
    (order: OrderWithManager): Manager | null =>
      keyOf(order) in assigned ? assigned[keyOf(order)] : (order.admin ?? null),
    [assigned],
  );

  /** The manager shown for the order: its own, else the client's. */
  const effectiveManager = useCallback(
    (order: OrderWithManager): Manager | null =>
      orderManager(order) ?? order.user?.manager ?? null,
    [orderManager],
  );

  const assign = async (order: OrderWithManager, manager: Manager | null) => {
    await setOrderManager({
      variables: {
        orderType: order.orderType,
        orderId: order.id,
        managerId: manager?.id ?? null,
      },
    });
    setAssigned((prev) => ({ ...prev, [keyOf(order)]: manager }));
  };

  return { orderManager, effectiveManager, assign };
};

export type OrderManagers = ReturnType<typeof useOrderManagers>;

export const ALL_MANAGERS = "all";
const NO_MANAGER = "none";

/**
 * The manager filter of a campaigns list: every manager these orders know,
 * their own or their client's, so a choice stays listed after a reassignment.
 */
export const managerFilterOptions = (
  orders: OrderWithManager[],
  orderManager: OrderManagers["orderManager"],
) => {
  const byId = new Map<number, Manager>();
  for (const order of orders) {
    for (const manager of [
      orderManager(order),
      order.admin,
      order.user?.manager,
    ]) {
      if (manager) byId.set(manager.id, manager);
    }
  }
  const named = Array.from(byId.values())
    .map((manager) => ({
      value: String(manager.id),
      label: managerName(manager),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return [
    { value: ALL_MANAGERS, label: "All managers" },
    { value: NO_MANAGER, label: "No manager" },
    ...named,
  ];
};

/** Whether the manager shown for an order is the one picked in the filter. */
export const matchesManager = (selected: string, manager: Manager | null) =>
  selected === ALL_MANAGERS ||
  (manager ? String(manager.id) === selected : selected === NO_MANAGER);

export default useOrderManagers;
